import { ContextualMenu, IContextualMenuItem, ILinkStyles } from '@fluentui/react';
import {
  forwardRef,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useRef,
  useState,
} from 'react';

import { SafeExternalLink } from '@shared/lib/ui/components-alpha/SafeExternalLink';

import { IComponent, IconName, IStyleFunctionOrObject } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesIcon, buttonStylesIcon20 } from '../Button/Button.styles';
import { EmptyMessage, IEmptyMessageProps } from '../EmptyMessage/EmptyMessage';
import { Link } from '../Link/Link';
import { linkStylesBlack } from '../Link/Link.styles';
import { Loader } from '../Loader/Loader';
import { loaderStylesSpinnerHeight100, loaderStylesSpinnerXLarge } from '../Loader/Loader.styles';
import { IWidgetPanelClassNames, IWidgetPanelStyles, widgetPanelStylesDefault } from './WidgetPanel.styles';

export interface IWidgetPanelProps extends IComponent<IWidgetPanelStyles>, HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  linkStyles?: IStyleFunctionOrObject<ILinkStyles> | IStyleFunctionOrObject<ILinkStyles>[];
  linkText?: string;
  linkHref?: string;
  linkOnClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLElement>) => void;
  contextMenuItems?: IContextualMenuItem[];
  loadingMessage?: string;
  emptyMessageProps?: IEmptyMessageProps;
  actions?: ReactElement;
  leftLabel?: string;
  bottomLabel?: string;
  children?: ReactNode;
}

export const WidgetPanel = forwardRef((props: IWidgetPanelProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    className,
    title,
    description,
    contextMenuItems,
    linkStyles = linkStylesBlack,
    linkText = 'View all',
    linkHref,
    linkOnClick,
    loadingMessage,
    emptyMessageProps,
    actions,
    leftLabel,
    bottomLabel,
    children,
    ...elementProps
  } = props;
  const buttonRef = useRef(null);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const classNames = useClassNames<Partial<IWidgetPanelStyles>, IWidgetPanelClassNames>(
    'WidgetPanel',
    widgetPanelStylesDefault,
    styles,
    className,
    leftLabel && 'has-left-label',
    bottomLabel && 'has-bottom-label'
  );
  const onShowContextualMenu = useCallback((ev: MouseEvent<HTMLElement>) => {
    ev.preventDefault();
    setShowContextualMenu(true);
  }, []);
  const onHideContextualMenu = useCallback(() => setShowContextualMenu(false), []);
  return (
    <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'widget-panel'}>
      <div className={classNames.header}>
        <div className={classNames.titleAndDescription}>
          <div className={classNames.title}>{title}</div>
          {description && <div className={classNames.description}>{description}</div>}
        </div>
        <div className={classNames.controls}>
          {(linkHref || linkOnClick) && (
            <div className={classNames.link}>
              {linkHref ? (
                <SafeExternalLink href={linkHref} onClick={linkOnClick}>
                  {linkText}
                </SafeExternalLink>
              ) : (
                <Link styles={linkStyles} onClick={linkOnClick}>
                  {linkText}
                </Link>
              )}
            </div>
          )}
          {contextMenuItems && (
            <div className={classNames.button}>
              <Button
                styles={[buttonStylesIcon, buttonStylesIcon20]}
                iconName={IconName.More}
                ref={buttonRef}
                onClick={onShowContextualMenu}
              />
              <ContextualMenu
                items={contextMenuItems}
                hidden={!showContextualMenu}
                target={buttonRef}
                onItemClick={onHideContextualMenu}
                onDismiss={onHideContextualMenu}
              />
            </div>
          )}
          {actions && <div className={classNames.actions}>{actions}</div>}
        </div>
      </div>
      <div className={classNames.body}>
        {loadingMessage ? (
          <Loader styles={[loaderStylesSpinnerXLarge, loaderStylesSpinnerHeight100]} label={loadingMessage} />
        ) : emptyMessageProps ? (
          <EmptyMessage {...emptyMessageProps} />
        ) : (
          <>
            {children}
            {leftLabel && (
              <div className={classNames.leftLabel}>
                <div className={classNames.leftLabelText}>{leftLabel}</div>
              </div>
            )}
            {bottomLabel && <div className={classNames.bottomLabel}>{bottomLabel}</div>}
          </>
        )}
      </div>
    </div>
  );
});
